/**
 * Per-mission working state on linked docs. Ticks, uploads, and variable
 * values never write the doc, and they do not enter undo history.
 */

export function useMissionProgress(setLocalSprint) {
  /**
   * Per-mission tick overrides for linked Docs (Chunk 11b).
   * `task.docTicks` = { [docId]: { [checkboxBlockId]: boolean } } — only the
   * steps this mission has diverged from the doc's master defaults. The doc
   * itself is never written to. No undo entry: ticks are working progress,
   * not document edits.
   */
  const handleMissionDocTicksChange = (taskId, docId, overrides) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          const docTicks = { ...(t.docTicks || {}) };
          if (overrides && Object.keys(overrides).length > 0) {
            docTicks[docId] = overrides;
          } else {
            delete docTicks[docId]; // back in full agreement with the master
          }
          return { ...t, docTicks, updatedAt: new Date().toISOString() };
        }),
      };
    });
  };

  /**
   * Per-mission variable values for linked docs.
   * `task.variableValues` is a list of doc-value overlays (same shape as a
   * campaign `docOverlay`). Absent overlays fall back to the campaign value,
   * so missions that never edited a variable keep showing what they show today.
   */
  const handleMissionVariablesChange = (taskId, variableValues) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          const next = Array.isArray(variableValues) ? variableValues : [];
          return {
            ...t,
            variableValues: next.length ? next : [],
            updatedAt: new Date().toISOString(),
          };
        }),
      };
    });
  };

  /**
   * Per-mission file-upload values for linked Docs.
   * `task.docFiles` = { [docId]: { [fileUploadBlockId]: FileRef[] } }
   * — files this mission dropped into upload fields. The doc itself is
   * never written to. No undo entry: same as ticks (working progress).
   */
  const handleMissionDocFilesChange = (taskId, docId, overrides) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          const docFiles = { ...(t.docFiles || {}) };
          if (overrides && Object.keys(overrides).length > 0) {
            docFiles[docId] = overrides;
          } else {
            delete docFiles[docId];
          }
          return { ...t, docFiles, updatedAt: new Date().toISOString() };
        }),
      };
    });
  };

  return {
    handleMissionDocTicksChange,
    handleMissionDocFilesChange,
    handleMissionVariablesChange,
  };
}
